/**
 * SupplyPlansLibrarySection — Feb 2026 mid-month.
 *
 * Renders the founder-curated "Premade Supply Plans" library on the
 * Library "Supply Plans" tab. Signed-in paid teachers see a read-only
 * browse view + Download-PDF per plan. Admins additionally see per-slot
 * Seed / Regenerate / Delete controls.
 *
 * Data source:
 *   - Paid teacher: GET /api/premade-supply/library
 *   - Admin only:   GET /api/admin/premade-supply
 *   - PDF download: GET /api/premade-supply/library/{id}/pdf
 *   - Seed:         POST /api/admin/premade-supply/seed-slot {grade,slot}
 *   - Regenerate:   POST /api/admin/premade-supply/regenerate/{id}
 *   - Delete:       DELETE /api/admin/premade-supply/{id}
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardList,
  Download,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];
const SLOTS_PER_GRADE = 5;

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function SupplyPlansLibrarySection() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null); // "grade|slot" key while a slot is mutating

  const fetchLibrary = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin ? "/admin/premade-supply" : "/premade-supply/library";
      const r = await fetch(`${apiBase()}${endpoint}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setGrouped(data?.grades || {});
    } catch (err) {
      console.error("Failed to load supply-plan library:", err);
      setGrouped({});
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  const totalActive = useMemo(
    () => Object.values(grouped).reduce((n, arr) => n + (arr?.length || 0), 0),
    [grouped],
  );

  const handleSeed = async (grade, slot) => {
    const key = `${grade}|${slot}`;
    setBusySlot(key);
    try {
      const r = await fetch(`${apiBase()}/admin/premade-supply/seed-slot`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grade, slot }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Seeding ${grade} slot ${slot}…`, {
        description: "This runs in the background (~90s). Refresh in a minute.",
      });
    } catch (err) {
      toast.error("Couldn't start seed job", { description: err?.message });
    } finally {
      setBusySlot(null);
    }
  };

  const handleRegenerate = async (planId, grade, slot) => {
    const key = `${grade}|${slot}`;
    setBusySlot(key);
    try {
      const r = await fetch(`${apiBase()}/admin/premade-supply/regenerate/${planId}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Regenerating ${grade} slot ${slot}…`, {
        description: "Refresh in a minute to see the new version.",
      });
    } catch (err) {
      toast.error("Couldn't start regeneration", { description: err?.message });
    } finally {
      setBusySlot(null);
    }
  };

  const handleDelete = async (planId, grade, slot) => {
    if (!window.confirm(`Delete ${grade} slot ${slot}? Existing delivery links still work.`)) return;
    const key = `${grade}|${slot}`;
    setBusySlot(key);
    try {
      const r = await fetch(`${apiBase()}/admin/premade-supply/${planId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Slot removed.");
      await fetchLibrary();
    } catch (err) {
      toast.error("Couldn't delete slot", { description: err?.message });
    } finally {
      setBusySlot(null);
    }
  };

  if (loading) {
    return (
      <div className="ae-card p-10 text-center mt-4" data-testid="supply-library-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2 text-[#4a6984]" />
        <span className="text-[#4a6984]/70 font-medium">Loading supply plans…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4" data-testid="supply-library-section">
      <div className="ae-card p-5 flex items-start gap-4">
        <div className="flex-shrink-0 p-2.5 rounded-md bg-gradient-to-br from-[#96ded1]/40 to-[#96ded1]/10">
          <ClipboardList className="h-5 w-5 text-[#1c2d3d]" />
        </div>
        <div className="flex-1">
          <h3 className="font-extrabold text-[#1c2d3d]">Founder-curated supply plans</h3>
          <p className="text-sm text-[#4a6984] mt-1">
            Full-day plans for Grades 4–8 (250 minutes, worksheets, answer
            keys, closing routines). Print, hand it to your sub, done.
            {totalActive > 0 && (
              <> Currently {totalActive} of {GRADES.length * SLOTS_PER_GRADE} slots live.</>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {GRADES.map((grade) => {
          const rows = grouped[grade] || [];
          const bySlot = new Map(rows.map((r) => [r.slot, r]));
          const slotIndices = Array.from({ length: SLOTS_PER_GRADE }, (_, i) => i + 1);
          return (
            <div
              key={grade}
              className="ae-card overflow-hidden"
              data-testid={`supply-library-grade-${grade.replace(" ", "-").toLowerCase()}`}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-[#f4f5f7] border-b border-[#4a6984]/10">
                <h4 className="font-extrabold text-[#1c2d3d] tracking-tight">{grade}</h4>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                  {rows.length} / {SLOTS_PER_GRADE} slots
                </span>
              </div>
              <ul className="divide-y divide-[#4a6984]/10">
                {slotIndices.map((slot) => {
                  const plan = bySlot.get(slot);
                  const key = `${grade}|${slot}`;
                  const busy = busySlot === key;
                  return (
                    <li
                      key={slot}
                      className="flex items-center gap-3 px-4 py-3"
                      data-testid={`supply-slot-${grade.replace(" ", "-").toLowerCase()}-${slot}`}
                    >
                      <span className="w-14 flex-shrink-0 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/55">
                        Slot {slot}
                      </span>
                      <div className="flex-1 min-w-0">
                        {plan ? (
                          <>
                            <p className="font-bold text-[#1c2d3d] truncate">
                              {plan.activity_name || "(untitled)"}
                            </p>
                            <p className="text-[11px] text-[#4a6984]/70 mt-0.5">
                              Added {formatDate(plan.created_at)}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-[#4a6984]/60 italic">Empty slot</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {plan ? (
                          <>
                            <a
                              href={`${apiBase()}/premade-supply/library/${plan.id}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`supply-pdf-${plan.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#1c2d3d] bg-[#96ded1]/40 hover:bg-[#96ded1]/60 rounded-md transition"
                            >
                              <Download className="h-3.5 w-3.5" />
                              PDF
                            </a>
                            {isAdmin && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleRegenerate(plan.id, grade, slot)}
                                  disabled={busy}
                                  data-testid={`supply-regenerate-${plan.id}`}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] hover:bg-[#f4f5f7] rounded-md transition disabled:opacity-40"
                                  aria-label="Regenerate slot"
                                >
                                  {busy ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(plan.id, grade, slot)}
                                  disabled={busy}
                                  data-testid={`supply-delete-${plan.id}`}
                                  className="p-1.5 text-[#4a6984]/60 hover:text-[#b85b5b] hover:bg-[#fdf3f3] rounded-md transition disabled:opacity-40"
                                  aria-label="Delete slot"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleSeed(grade, slot)}
                              disabled={busy}
                              data-testid={`supply-seed-${grade.replace(" ", "-").toLowerCase()}-${slot}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#1c2d3d] bg-[#96ded1]/40 hover:bg-[#96ded1]/60 rounded-md transition disabled:opacity-40"
                            >
                              {busy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Sparkles className="h-3.5 w-3.5" />
                              )}
                              Seed
                            </button>
                          )
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
